import { Periods } from 'components/ProFormaReturnsTable/constant';
import { ICashFlowPeriod, IPerLoanReturns, IProFormaReturnsData } from 'views/ProFormaTable/types';

const DEFAULT_PERMANENT_LOAN_LABEL = 'Permanent loan';

export const getPermanentLoanLabel = (loan: IPerLoanReturns) => (loan.name ? loan.name : DEFAULT_PERMANENT_LOAN_LABEL);

export const orderPermanentDebts = (permanentDebts: IPerLoanReturns[]) => {
    const seniorLoans = permanentDebts.filter(loan => loan.isSenior === true);
    const otherLoans = permanentDebts
        .filter(loan => loan.isSenior !== true)
        .sort((a, b) => {
            if (a.payoffPriority === null && b.payoffPriority === null) return 0;
            if (a.payoffPriority === null) return 1;
            if (b.payoffPriority === null) return -1;
            return a.payoffPriority - b.payoffPriority;
        });
    return [...seniorLoans, ...otherLoans];
};

export const defaultPeriods = {
    'Jan 2023': 4364.63214488499,
    'Mar 2023': 2128.4748489905073,
    'Apr 2023': 1878.1144712397906,
    'May 2023': 3925.12447987615,
    'Jun 2023': 2294.2972849011485,
    'Jul 2023': 2295.6832216898906,
    'Aug 2023': 1893.506957332425,
    'Sep 2023': 2013.0654425893654,
    'Oct 2023': 2181.3692314871087,
    'Nov 2023': 1932.610521673614,
};

export const defaultReturnsData = {
    proFormaReturns: {
        totalDevelopmentCost: {
            amount: 0,
            periods: {},
        },
        totalInterest: {
            amount: 0,
            periods: {},
        },
        totalLoanDrawsDuringDevelopment: {
            amount: 0,
            periods: {},
        },
        permLoanFunding: {
            amount: 0,
            periods: {},
        },
        cashFlowFromOperations: {
            amount: 0,
            periods: {},
        },
        permLoanDebtService: {
            amount: 0,
            periods: {},
        },
        netSaleProceeds: {
            amount: 0,
            periods: {},
        },
        constructionLoanPayoff: {
            amount: 0,
            periods: {},
        },
        permanentLoanPayoff: {
            amount: 0,
            periods: {},
        },
        equityProceedsFromCapitalEvents: {
            amount: 0,
            periods: {},
        },
    },
    cashFlowData: {
        netUnleveredCashFlow: {
            amount: 0,
            equityMultiple: 0,
            irr: 0,
            netProfit: 0,
            periods: {},
        },
        netLeveredCashFlow: {
            amount: 0,
            equityMultiple: 0,
            irr: 0,
            netProfit: 0,
            periods: {},
        },
    },
    costMetrics: {
        yieldOnCost: {
            untrended: 0,
            trended: 0,
            sale: 0,
            isHighlighted: null,
        },
        marketCapRate: {
            untrended: 0,
            trended: 0,
            sale: 0,
            isHighlighted: null,
        },
        developmentSpread: {
            untrended: 0,
            trended: 0,
            sale: 0,
            isHighlighted: null,
        },
    },
};

export const buildTransformedReturnsData = (returnsData: IProFormaReturnsData, cadence: string) => {
    const cashFlow = cadence === Periods.MONTHLY ? returnsData.cashFlow : returnsData.yearlyCashFlow;

    const createPeriods = (key: keyof ICashFlowPeriod, source: ICashFlowPeriod[] | undefined = cashFlow) => {
        const periodsData: Record<string, number | null> = {};
        source?.forEach(data => {
            if (data?.analysisDate) {
                periodsData[data.analysisDate] = data?.[key] === null ? null : Number(data?.[key]);
            }
        });
        return periodsData;
    };

    const orderedPermanentDebts = orderPermanentDebts(returnsData.permanentDebts ?? []);

    const permanentDebtRowsByLineItem: Record<string, Array<{ key: string; label: string }>> = {
        totalPermanentLoanClosingCosts: [],
        totalPermanentLoanFunding: [],
        totalPermanentLoanDebtService: [],
        totalPermanentLoanPayoff: [],
    };
    const permanentDebtAmounts: Record<string, number | null> = {};
    const permanentDebtPeriods: Record<string, Record<string, number | null>> = {};

    orderedPermanentDebts.forEach((loan, index) => {
        const label = getPermanentLoanLabel(loan);
        const loanCashFlow = cadence === Periods.MONTHLY ? loan.cashFlow : loan.yearlyCashFlow;
        const closingCostsKey = `permanentLoan_${index}_closingCosts`;
        const fundingKey = `permanentLoan_${index}_funding`;
        const debtServiceKey = `permanentLoan_${index}_debtService`;
        const payoffKey = `permanentLoan_${index}_payoff`;

        permanentDebtRowsByLineItem.totalPermanentLoanClosingCosts.push({ key: closingCostsKey, label: `${label} closing costs` });
        permanentDebtRowsByLineItem.totalPermanentLoanFunding.push({ key: fundingKey, label: `${label} funding` });
        permanentDebtRowsByLineItem.totalPermanentLoanDebtService.push({ key: debtServiceKey, label: `${label} debt service` });
        permanentDebtRowsByLineItem.totalPermanentLoanPayoff.push({ key: payoffKey, label: `${label} payoff` });

        permanentDebtAmounts[closingCostsKey] = loan.totalClosingCosts ?? null;
        permanentDebtAmounts[fundingKey] = loan.totalFunding ?? null;
        permanentDebtAmounts[debtServiceKey] = loan.totalDebtService ?? null;
        permanentDebtAmounts[payoffKey] = loan.totalPayoff ?? null;

        permanentDebtPeriods[closingCostsKey] = createPeriods('permanentLoanClosingCosts', loanCashFlow);
        permanentDebtPeriods[fundingKey] = createPeriods('permanentLoanFunding', loanCashFlow);
        permanentDebtPeriods[debtServiceKey] = createPeriods('permanentLoanDebtService', loanCashFlow);
        permanentDebtPeriods[payoffKey] = createPeriods('permanentLoanPayoff', loanCashFlow);
    });

    const proFormaReturnsCashFlow: Record<string, Record<string, number | null>> = {
        totalProjectCost: createPeriods('projectCost'),
        totalLoanInterest: createPeriods('constructionLoanInterest'),
        totalFinancingFees: createPeriods('financingFee'),
        totalConstructionLoanDraws: createPeriods('constructionLoanDraws'),
        totalCashFlowFromOperations: createPeriods('cashFlowFromOperations'),
        totalNetSaleProceeds: createPeriods('netSaleProceeds'),
        totalConstructionLoanPayoff: createPeriods('constructionLoanPayoff'),
        totalEquityProceedFromCapitalEvents: createPeriods('equityProceedFromCapitalEvents'),
        totalOtherRevenue: createPeriods('otherRevenue'),
        totalSaleProceeds: createPeriods('grossSaleProceeds'),
        closingCosts: createPeriods('closingCosts'),
        totalBuilderDepositCredit: createPeriods('builderDepositCredit'),
        ...permanentDebtPeriods,
    };

    const proFormaReturns: Record<string, number | string | null> = {
        totalProjectCost: returnsData.totalProjectCost ?? null,
        totalLoanInterest: returnsData.totalLoanInterest ?? null,
        totalFinancingFees: returnsData.totalFinancingFees ?? null,
        totalConstructionLoanDraws: returnsData.totalConstructionLoanDraws ?? null,
        totalCashFlowFromOperations: returnsData.totalCashFlowFromOperations ?? null,
        totalNetSaleProceeds: returnsData.totalNetSaleProceeds ?? null,
        totalConstructionLoanPayoff: returnsData.totalConstructionLoanPayoff ?? null,
        totalEquityProceedFromCapitalEvents: returnsData.totalEquityProceedFromCapitalEvents ?? null,
        // Land development specific scalar fields
        totalSaleProceeds: returnsData.totalSaleProceeds ?? null,
        totalGrossSaleProceeds: returnsData.totalGrossSaleProceeds ?? null,
        closingCosts: returnsData.closingCosts ?? null,
        totalBuilderDepositCredit: returnsData.totalBuilderDepositCredit ?? null,
        totalOtherRevenue: returnsData.totalOtherRevenue ?? null,
        ...permanentDebtAmounts,
    };

    return {
        proFormaReturns,
        permanentDebtRowsByLineItem,
        proFormaReturnsCashFlow,
        cashFlowData: {
            netUnleveredCashFlow: {
                equityMultiple: returnsData.netUnleveredEquityMultiple ?? null,
                irr: returnsData.netUnleveredIrr ?? null,
                netProfit: returnsData.netUnleveredNetProfit ?? null,
                amount: returnsData.totalNetUnleveredCashFlow ?? null,
                periods: createPeriods('netUnleveredCashFlow'),
            },
            netLeveredCashFlow: {
                equityMultiple: returnsData.netLeveredEquityMultiple ?? null,
                irr: returnsData.netLeveredIrr ?? null,
                amount: returnsData.totalNetLeveredCashFlow ?? null,
                netProfit: returnsData.netLeveredNetProfit ?? null,
                periods: createPeriods('netLeveredCashFlow'),
            },
        },
        costMetrics: {
            yieldOnCost: {
                untrended: returnsData.yieldOnCost?.untrended ?? null,
                trended: returnsData.yieldOnCost?.trended ?? null,
                sale: returnsData.yieldOnCost?.sale ?? null,
                isHighlighted: null,
            },
            marketCapRate: {
                untrended: returnsData.marketCapitalizationRate?.current ?? null,
                trended: returnsData.marketCapitalizationRate?.trended ?? null,
                sale: returnsData.marketCapitalizationRate?.onSale ?? null,
                isHighlighted: null,
            },
            developmentSpread: {
                untrended: returnsData.developmentSpread?.untrended ?? null,
                trended: returnsData.developmentSpread?.trended ?? null,
                sale: returnsData.developmentSpread?.sale ?? null,
                isHighlighted: null,
            },
        },
    };
};

export const transformReturnsData = (returnsData: IProFormaReturnsData, cadence: string) => {
    if (!returnsData) {
        return defaultReturnsData;
    }
    return buildTransformedReturnsData(returnsData, cadence);
};

export const getPeriodKeys = (returnsData: IProFormaReturnsData, cadence: string, maxIndex?: number) => {
    const cashFlow = cadence === Periods.MONTHLY ? returnsData.cashFlow : returnsData.yearlyCashFlow;
    if (!cashFlow?.length || cashFlow.length === 1) return [];
    const dates = cashFlow.map(cf => cf.analysisDate);
    return maxIndex !== undefined ? dates.slice(0, maxIndex + 1) : dates;
};
